import { LegalPageLayout } from '../components/layout/LegalPageLayout';
import { USPSection } from '../components/home/USPSection';
import { Linkedin, Mail, HeartHandshake, Cog, Users, Coffee } from 'lucide-react';
import logo from '../assets/logo.webp';
import authorPhoto from '../assets/Photo.jpg';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

const LINKEDIN_URL = 'https://www.linkedin.com/in/loginov-vladimir/';

export const AboutPage: React.FC = () => {
  const navigate = useNavigate();
  
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const handleSupportClick = () => {
    window.scrollTo(0, 0);
    navigate('/support');
  };

  const handleExternalLink = () => {
    window.scrollTo(0, 0);
  };

  return (
  <>
    <Helmet prioritizeSeoTags>
      <title>About Cognitive Bias Lab | Our Mission & Approach</title>
      <meta name="description" content="Discover Cognitive Bias Lab's mission to make psychology accessible through interactive learning. Learn our approach to teaching about cognitive biases." />
      <meta name="keywords" content="cognitive bias education, psychology learning platform, bias awareness, critical thinking development, educational psychology" />
      <link rel="canonical" href="https://www.cognitivebiaslab.com/about/" key="canonical" data-source="page-component" />
      {/* OpenGraph tags for better social sharing */}
      <meta property="og:title" content="About Cognitive Bias Lab | Our Mission and Values" />
      <meta 
        property="og:description" 
        content="Discover Cognitive Bias Lab's mission to make psychology accessible through interactive learning. Learn our approach to teaching about cognitive biases." 
      />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://www.cognitivebiaslab.com/about/" />
      <meta property="og:site_name" content="Cognitive Bias Lab" />
      <meta property="og:image" content="https://www.cognitivebiaslab.com/logo.webp" />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:image:type" content="image/webp" />
      
      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content="About Cognitive Bias Lab | Our Mission and Values" />
      <meta 
        name="twitter:description" 
        content="Discover Cognitive Bias Lab's mission to make psychology accessible through interactive learning. Learn our approach to teaching about cognitive biases." 
      />
      <meta name="twitter:image" content="https://www.cognitivebiaslab.com/logo.webp" />
    </Helmet>

    <LegalPageLayout title="About Cognitive Bias Lab" useH1={true}>
      <div className="space-y-12 max-w-4xl mx-auto">
        {/* Welcome Section */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold text-gray-800">Welcome!</h2>
          <p className="text-gray-600 leading-relaxed">
            Cognitive Bias Lab is your interactive space to explore the fascinating world of cognitive biases. 
            I created this website because, like many others, I've always been captivated by how our minds can 
            be tricked by our own biases. Yet, I couldn't find a resource that presented these concepts in a 
            dynamic, engaging way. So, I took it upon myself to develop a platform that makes learning about 
            these intriguing mental shortcuts both accessible and fun.
          </p>
        </section>

        {/* Mission Section with Logo */}
        <section className="flex flex-col md:flex-row gap-8 items-center">
          <div className="space-y-4 flex-1">
            <h2 className="text-2xl font-bold text-gray-800">Our Mission</h2>
            <p className="text-gray-600 leading-relaxed">
              I'm a firm believer in the power of education. Cognitive Bias Lab exists to offer free, engaging, 
              and exciting educational content that demystifies complex ideas. My hope is that this resource will 
              empower you with insights to better understand how cognitive biases shape our decisions and perceptions, 
              ultimately helping you navigate the world with a sharper, more informed mind.
            </p>
          </div>
          <img 
            src={logo} 
            alt="Cognitive Bias Lab" 
            className="w-48 h-48 object-contain"
            loading="lazy"
            decoding="async"
          />
        </section>

        {/* Author Section with Photo */}
        <section className="flex flex-col md:flex-row gap-8 items-start">
          <div className="space-y-4 flex-1">
            <h2 className="text-2xl font-bold text-gray-800">About the Author</h2>
            <p className="text-gray-600 leading-relaxed">
              Hello, I'm Vladimir Loginov, the founder of Cognitive Bias Lab. With a background as a product manager 
              and a passion for clear communication and innovative thinking, I've been fortunate to study at 
              Trinity College Dublin and Bauman Moscow State Technical University. My diverse educational and 
              professional experiences have shaped my approach to understanding and sharing complex ideas.
            </p>
            <div className="flex items-center gap-2">
              <Linkedin className="w-5 h-5 text-orange-500" />
              <a 
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer" 
                className="text-orange-500 hover:text-orange-600"
                onClick={handleExternalLink}
              >
                Connect on LinkedIn
              </a>
            </div>
          </div>
          <img 
            src={authorPhoto} 
            alt="Vladimir Loginov" 
            className="w-48 h-48 object-cover rounded-xl shadow-md shrink-0"
            loading="lazy"
            decoding="async"
            sizes="(max-width: 640px) 192px, 192px"
          />
        </section>

        {/* Values Section with Icons */}
        <section className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-800">My Values</h2>
          <p className="text-gray-600">
            Inspired by Andrey Sakharov's essay "Peace, Progress and Human Rights," my personal values guide 
            every aspect of this project. I believe in:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-3 p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow border border-orange-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-gradient-to-br from-orange-100 to-pink-100 rounded-xl p-3 w-12 h-12 flex items-center justify-center">
                  <HeartHandshake className="w-6 h-6 text-orange-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">Peace</h3>
              </div>
              <p className="text-gray-700">Promoting understanding and empathy through open dialogue.</p>
            </div>
            <div className="space-y-3 p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow border border-orange-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-gradient-to-br from-orange-100 to-pink-100 rounded-xl p-3 w-12 h-12 flex items-center justify-center">
                  <Cog className="w-6 h-6 text-orange-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">Progress</h3>
              </div>
              <p className="text-gray-700">Embracing continuous learning and innovation to improve how we think.</p>
            </div>
            <div className="space-y-3 p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow border border-orange-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-gradient-to-br from-orange-100 to-pink-100 rounded-xl p-3 w-12 h-12 flex items-center justify-center">
                  <Users className="w-6 h-6 text-orange-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">Human Rights</h3>
              </div>
              <p className="text-gray-700">Upholding the belief that everyone deserves access to quality education.</p>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold text-gray-800">Get in Touch</h2>
          <p className="text-gray-600 leading-relaxed">
            I truly value your feedback and ideas. Whether you have suggestions to improve the website, questions 
            about the content, or simply wish to share your thoughts, I'm all ears. Please feel free to drop me 
            a message through the contact page or reach out via LinkedIn. Your insights will help make Cognitive 
            Bias Lab even better.
          </p>
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-orange-500" />
            <a 
              href={`mailto:${encodeURIComponent('contact@cognitivebiaslab.com')}?subject=${encodeURIComponent('Feedback for Cognitive Bias Lab')}`}
              className="text-orange-500 hover:text-orange-600"
              onClick={handleExternalLink}
            >
              Send me an email
            </a>
          </div>
        </section>

        {/* Support Section with Button */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold text-gray-800">Support This Project</h2>
          <p className="text-gray-600 leading-relaxed">
            Cognitive Bias Lab is a free educational resource created with passion and dedication. If you find value in this project 
            and would like to support its continued development, consider buying me a coffee. Your contribution helps keep this 
            resource accessible to everyone while enabling me to add new content and features.
          </p>
          <div className="flex items-center gap-2">
            <Coffee className="w-5 h-5 text-orange-500" />
            <a 
              onClick={handleSupportClick}
              className="text-orange-500 hover:text-orange-600 cursor-pointer"
            >
              Buy Me a Coffee
            </a>
          </div>
        </section>

        <p className="text-gray-600 italic">Thank you for stopping by—let's explore the quirks of our minds together!</p>
      </div>
    </LegalPageLayout>

    {/* Add USP section at the bottom */}
    <div className="mt-4">
      <USPSection showTitle={false} />
    </div>
  </>
  );
}; 